import { FC, useMemo } from "react"
import { Box, styled } from "@mui/material"
import { DragContainerComponent } from "../../ui/containers/DragContainer"
import { DropContainerComponent } from "../../ui/containers/DropContainer"
import { addUniqPartId } from "../../helpers"
import {
  CheckCorrect,
  isQuestionComplete,
  NeutralSetting,
  ShowHide
} from "../../types"
import { useGapContextValue } from "../GapMatchInteraction"

interface GapComponentProps {
  identifier: string
  templateidentifier: string
  showhide: ShowHide
  matchgroup: string
  required: string
  fluid?: boolean
  checkCorrect?: CheckCorrect
  isCompletedQuestion?: isQuestionComplete
  isNeutralSetting?: NeutralSetting
}

export const GapComponent: FC<GapComponentProps> = ({
  identifier,
  fluid = false,
  checkCorrect,
  isCompletedQuestion,
  isNeutralSetting,
  ...props
}) => {
  const {
    fluidGaps,
    containers,
    scoredContainers,
    variants,
    isFinished,
    isMaxMatches,
    handleRemove,
    clickMovingState: { activeGapBlockId, activeGapId },
    containersArray,
    variantsArray,
    groupMode
  } = useGapContextValue()

  const dropVariant = useMemo(
    () =>
      containers &&
      variants &&
      variants.find(
        variant =>
          variant.props.identifier ===
          (groupMode
            ? scoredContainers[identifier]?.correctAnswer
            : containers[identifier])
      ),
    [containers, variants, identifier, groupMode, scoredContainers]
  )

  const containerIndex = useMemo(
    () => containersArray.length && containersArray.indexOf(identifier),
    [containersArray, identifier]
  )

  const variantIndex = useMemo(
    () =>
      variantsArray.length &&
      dropVariant &&
      variantsArray.indexOf(dropVariant.props.identifier),
    [dropVariant, variantsArray]
  )

  const correctAnswer = useMemo(
    () =>
      variants.find(
        variant =>
          variant.props.identifier ===
          scoredContainers[identifier]?.correctAnswer
      )?.props?.children,
    [variants, scoredContainers, identifier]
  )

  const { questionCompleted } = isCompletedQuestion?.() ?? {
    questionCompleted: false
  }

  return (
    <StyledGap data-gap={identifier}>
      <StyledDropContainer
        id={identifier}
        containerIndex={containerIndex}
        assigned={!!dropVariant}
        disabled={
          isFinished || (isMaxMatches && !!!dropVariant) || questionCompleted
        }
      />
      {dropVariant && (
        <DragContainerComponent
          id={addUniqPartId(dropVariant.props.identifier)}
          containerId={identifier}
          assigned={!!dropVariant}
          disabled={isFinished || questionCompleted}
          handleRemove={handleRemove(dropVariant.props.identifier, identifier)}
          selected={
            activeGapBlockId === dropVariant.props.identifier &&
            activeGapId === identifier
          }
          completed={questionCompleted}
          isCorrect={checkCorrect(
            `${dropVariant.props.identifier} ${identifier}`
          )}
          variantIndex={variantIndex}
          fluid={fluidGaps}
          correct={scoredContainers[identifier]?.correct}
          correctAnswer={correctAnswer}
          groupMode={groupMode}
          isNeutralSetting={isNeutralSetting}
        >
          {dropVariant}
        </DragContainerComponent>
      )}
    </StyledGap>
  )
}

const StyledGap = styled(Box)(({ theme }) => ({
  position: "relative",
  display: "inline-block",
  verticalAlign: "middle",
  margin: theme.spacing(1)
}))

const StyledDropContainer = styled(DropContainerComponent)(({ assigned }) => ({
  ...(assigned && {
    position: "absolute",
    left: 0,
    top: 0,
    height: "100%",
    width: "100%"
  })
}))
